/**
 * Client-side mirror of the admin route contract (`/pnp/admin/route/*`).
 *
 * The server re-validates everything and stays the authority — these rules exist so the form can
 * reject an obviously bad record without spending a round trip, and so the copy explaining each
 * rule lives next to the rule itself.
 */

/** `parent` adds a new top-level nav group; `sub` adds an option inside an existing one. */
export type AdminRouteLevel = 'parent' | 'sub';

export const ADMIN_ROUTE_QUERY_KEYS = {
    /**
     * The whole route list. Deliberately one key: `ConfigContext` reads it to build `appConfig`
     * and the admin screen reads it to render the table, so invalidating after a write repaints
     * the navbar and the table from the same response.
     */
    list: ['adminRoutes', 'list'],
};

/** Route rows change only when an admin edits them, so a long window is safe. */
export const ADMIN_ROUTE_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * `route_id` becomes the `id` on both the route entry and the nav option — the key everything
 * else joins on — so it has to be stable, URL-safe and unique within the tenant.
 */
export const ROUTE_ID_PATTERN = /^[A-Za-z][A-Za-z0-9_-]{1,63}$/;
export const ROUTE_ID_MAX_LENGTH = 64;
export const ROUTE_ID_RULE_MESSAGE =
    'Use 2-64 characters — letters, digits, hyphen or underscore — starting with a letter.';

/**
 * Stored bare, no leading slash, matching `screenDetailsWithRoutes[].path`
 * (`pricing/analytics/price-change`). `toAbsolute()` adds the slash at render time.
 *
 * `:` is rejected on purpose: `buildRouteUrlLookup` skips parameterised paths, so a route with a
 * dynamic segment would register but could never be linked to from the navbar.
 */
export const ROUTE_PATH_SEGMENT_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const ROUTE_PATH_MAX_LENGTH = 200;
export const ROUTE_PATH_RULE_MESSAGE =
    'Use lowercase segments separated by "/", e.g. pricing/analytics/competitor-benchmarking.';

export const ROUTE_LABEL_MAX_LENGTH = 60;
export const ROUTE_DESCRIPTION_MAX_LENGTH = 140;

/**
 * Column a subroute lands in when its parent has none yet — the case for a top-level route an
 * admin created earlier and is now nesting under. Never rendered: admin-created parent groups
 * carry `showSubHeader: false`.
 */
export const DEFAULT_ROUTE_SECTION = 'Screens';

/**
 * A page blocked by `X-Frame-Options` or CSP `frame-ancestors` never fires the iframe's `error`
 * event — the block happens below the JS layer — and cross-origin `contentDocument` access throws.
 * So the preview waits this long for a `load` and calls it blocked if none arrives.
 */
export const EMBED_PREVIEW_TIMEOUT_MS = 10000;

/** Keystroke settle time before the preview frame is remounted on a new URL. */
export const EMBED_PREVIEW_DEBOUNCE_MS = 600;
